import type { Event } from "@bugsnag/js";
import type { PxlRequestInterceptorContext } from "./index";
import useUserStore from "~/stores/user";

export const setAuthorizationInterceptor = async (ctx: PxlRequestInterceptorContext) => {
  const { fetchOptions, requestOptions } = ctx;
  if (requestOptions.withToken) {
    fetchOptions.headers = {
      ...fetchOptions.headers,
      "X-Authorization": `Bearer ${requestOptions.withToken}`,
    };
    return;
  }

  const accessToken = await getCurrentAccessToken(requestOptions.forceRefreshToken);

  if (!accessToken)
    return;

  fetchOptions.headers = {
    ...fetchOptions.headers,
    "X-Authorization": `Bearer ${accessToken}`,
  };
};

async function getCurrentAccessToken(forceRefreshToken = false) {
  const userStore = useUserStore();
  const token = userStore.userTokenData?.token || userStore.token;

  if (!token)
    return null;

  const isTokenExpired = checkAccessTokenExpiration(userStore.userTokenData?.tokenExpirationTime, userStore.userTokenData?.tokenUpdateTime);

  if (isTokenExpired || forceRefreshToken)
    await userStore.refreshAccessToken();

  return userStore.userTokenData?.token;
}

export function setBugsnagErrorInterceptor(fetchError: Response) {
  const isServerError = fetchError.status >= 500;
  const isDisableErrorTracking = process.env.NUXT_PUBLIC_DISABLE_ERROR_TRACKING === "true";

  if (isDisableErrorTracking || !isServerError)
    return;

  try {
    const bugsnag = useBugsnag();

    if (!bugsnag)
      return;

    const bugsnagMetaData = {
      statusCode: fetchError.status,
      headers: fetchError.headers,
      statusText: fetchError.statusText,
      url: fetchError.url,
    };

    const error = new Error(`HTTP ${fetchError.status} ${fetchError.statusText}`);
    bugsnag.notify(error, (event: Event) => event.addMetadata("metadata", { bugsnagMetaData }));
  }
  catch (error) {
    console.warn("Bugsnag not available:", error);
  }
}
